<template>
  <div class="user-layout mb-40">
    <ValidationObserver v-slot="{ invalid, passes }" ref="form">
    <form v-if="user"
          class="position-relative mb-40"
          @submit.prevent="passes(createUpdate)">
      <badge-block no_padding>
        <default-box>
          <default-title h_tag="h3">
            {{ $t('user.user_info') }}
          </default-title>
          <div class="row mt-20">
            <div class="col-md-6 col-12 mb-10">
              <ValidationProvider rules="required" vid="name" name="name" v-slot="{ errors }">
              <default-input
                :label="$t('user.name')"
                required
                v-model="user.name"
              />
                <span>{{ errors[0] }}</span>
              </ValidationProvider>
            </div>
            <div class="col-md-6 col-12 mb-10">
              <ValidationProvider rules="required" vid="surname" name="surname" v-slot="{ errors }">
              <default-input
                :label="$t('user.surname')"
                required
                v-model="user.surname"
              />
                <span>{{ errors[0] }}</span>
              </ValidationProvider>
            </div>

            <div class="col-md-6 col-12 mb-10">
              <ValidationProvider rules="required" vid="email" name="email" v-slot="{ errors }">
              <default-input
                :label="$t('inputs.email')"
                required
                type="email"
                v-model="user.email"
              />
                <span>{{ errors[0] }}</span>
              </ValidationProvider>
            </div>

            <div
              class="col-md-3 col-12 mb-10">
              <ValidationProvider vid="user.role" name="user.role" v-slot="{ errors }">
              <default-select
                :placeholder="$t('user.role')"
                :selected_val="user.role"
                required
                display_item_api_key="name"
                :list_outer="roles"
                @change="changeRole"
              />
                <span>{{ errors[0] }}</span>
              </ValidationProvider>
            </div>

            <div
              v-if="getUserRoleIsAdmin"
              class="col-md-3 col-12 mb-10">
              <ValidationProvider vid="user_active" name="user_active" v-slot="{ errors }">
              <default-select
                :placeholder="$t('user.active')"
                :selected_val="user_active"
                required
                display_item_api_key="title"
                :list_outer="active"
                @change="changeActive"
              />
                <span>{{ errors[0] }}</span>
              </ValidationProvider>
            </div>
          </div>
        </default-box>

        <default-box class="mt-15">
          <default-title h_tag="h4">
            {{ $t('user.user_committees') }}
          </default-title>
          <div class="row mt-20">
            <div
              class="col-md-6 col-12 mb-10">
              <default-select
                searchable
                multiple
                :placeholder="$t('user.manager_of_committee')"
                :selected_val="committeeManager"
                display_item_api_key="title"
                :list_outer="committees"
                @change="changeCommitteeManager"
              />
            </div>

            <div
              class="col-md-6 col-12 mb-10">
              <default-select
                :placeholder="$t('user.employee_of_committee')"
                searchable
                allow_deselect
                display_item_api_key="title"
                :selected_val="committeeEmployee"
                :list_outer="committees"
                @change="changeCommitteeEmployee"
              />
            </div>
          </div>
        </default-box>

        <default-box class="mt-15">
          <default-title h_tag="h4">
            {{ $t('user.user_profile') }}
          </default-title>
          <div class="row mt-20">
            <div class="col-md-6 col-12 mb-10">
              <ValidationProvider :rules="{ regex: /^\+?[0-9]{10,12}$/ }" vid="user.profile.phone" name="user.profile.phone" v-slot="{ errors }">
              <default-input
                  :label="$t('user.phone')"
                  placeholder="+38(___)_______"
                  type="text"
                  size="13"
                  minlength="9"
                  maxlength="14"
                  pattern="[0-9]{3}-[0-9]{3}-[0-9]{4}"
                  v-model="user?.profile.phone"
              />
                <span>{{ errors[0] }}</span>
              </ValidationProvider>
            </div>
            <div class="col-md-6 col-12 mb-10">
              <ValidationProvider rules="required" vid="passport_details" name="passport_details" v-slot="{ errors }">
              <default-input
                  :label="$t('user.passport_details') + '*'"
                  v-model="user?.profile.passport_details"
              />
                <span>{{ errors[0] }}</span>
              </ValidationProvider>
            </div>
            <div class="col-md-6 col-12 mb-10">
              <ValidationProvider rules="required" vid="address" name="address" v-slot="{ errors }">
              <default-input
                  :label="$t('user.address') + '*'"
                  v-model="user?.profile.address"
              />
                <span>{{ errors[0] }}</span>
              </ValidationProvider>
            </div>
            <div
              class="col-md-3 col-12 mb-10">
              <ValidationProvider vid="profile.gender" name="profile.gender" v-slot="{ errors }">
              <default-select
                :placeholder="$t('user.gender')"
                :selected_val="gender"
                :list_outer="genders"
                required
                @change="changeGender"
              />
                <span>{{ errors[0] }}</span>
              </ValidationProvider>
            </div>
            <div
              class="col-md-3 col-12 mb-10">
              <ValidationProvider vid="profile.contract_type" name="profile.contract_type" v-slot="{ errors }">
              <default-select
                required
                :placeholder="$t('user.contract_type')"
                :selected_val="contract_type"
                :list_outer="contract_types"
                @change="changeContractType"
              />
                <span>{{ errors[0] }}</span>
              </ValidationProvider>
            </div>
          </div>
        </default-box>

        <default-box class="mt-15">
          <default-title h_tag="h4">
            {{ $t('user.user_vacation_configuration') }}
          </default-title>
          <div class="row row-gap-15 mt-20">
            <div class="col-md-6 col-12">
              <default-input
                  required
                  :input_props="{min: 0, max: 60}"
                  :label="$t('user.additional_vacation_days')"
                  type="number"
                  v-model="user.additional_vacation_days"
              />
            </div>
            <div class="col-md-6 col-12">
              <default-select
                required
                :placeholder="$t('user.weekdays_selectable')"
                :selected_val="get_weekdays_selectable"
                :list_outer="yes_no_statuses"
                @change="({id}) => user.allow_weekend_vacation = id"
              />
            </div>
          </div>
        </default-box>

        <default-box class="mt-15">
          <default-title h_tag="h4">
            {{ $t('user.user_poleweb_data') }}
          </default-title>
          <div class="row mt-20">
            <div class="col-md-6 col-12 mb-10">
              <default-input
                  :label="$t('user.id_poleweb')"
                  v-model="user?.profile.id_poleweb"
              />
            </div>
            <div class="col-md-6 col-12 mb-10">
              <default-input
                  :label="$t('user.individu_id_poleweb')"
                  v-model="user?.profile.individu_id_poleweb"
              />
            </div>
            <div class="col-md-6 col-12 mb-10">
              <label class="default-input">
                <span class="default-input__label">
                  {{$t('user.contract_start_date')}}
                  <span class="default-select_label-required">*</span>
                </span>
                <input class="default-input__block"
                       style="width: 100%;"
                       type="date"
                       required
                       v-model="user?.profile.contract_start_date">
              </label>
            </div>

          </div>
        </default-box>

        <default-box class="mt-15">
          <default-title h_tag="h4">
            {{ $t('user.user_benefit_packages') }}
          </default-title>
          <div class="row mt-20">
            <div
              class="col-md-6 col-12 mb-10">
              <default-select
                :placeholder="$t('user.package')"
                :selected_val="selectedBeltPackage.title"
                :list_outer="beltPackages"
                @change="changeBeltPackage"
              />
            </div>
            <div v-if="selectedBeltPackage.title"
                 class="col-md-6 col-12 mb-10">
              <default-input
                v-if="disabledBeltSelect"
                :label="$t('user.selected_belt')"
                v-model="selectedBeltBenefit.title"
                disabled
              />
              <default-select
                v-else
                :placeholder="$t('user.belt')"
                :selected_val="selectedBeltBenefit.title"
                display_item_api_key="title"
                :list_outer="selectedBeltPackage.benefits"
                @change="changeBeltBenefit"/>
              <label class="profile-component-date-input mt-10">
              <span title="Manager" class="default-input__label">
                {{ $t('inputs.start_date') }}
              </span>
                <ValidationProvider rules="required_if:disabledBeltSelect,true" vid="user.benefit_start_date" name="user.benefit_start_date" v-slot="{ errors }">
                <input type="date" id="start" name="trip-start"
                       v-model="user.benefit_start_date">
                  <span>{{ errors[0] }}</span>
                </ValidationProvider>
              </label>
              <label class="profile-component-date-input mt-10">
              <span title="Manager" class="default-input__label">
                {{ $t('inputs.end_date') }}
              </span>
                <ValidationProvider rules="required_if:disabledBeltSelect,true" vid="user.benefit_end_date" name="user.benefit_end_date" v-slot="{ errors }">
                <input type="date" id="end" name="trip-start"
                       v-model="user.benefit_end_date">
                <span>{{ errors[0] }}</span>
                </ValidationProvider>
              </label>
            </div>
          </div>
        </default-box>
        <div class="text-end mt-20 sticky-bottom">
          <default-button
            button_large
            :disabled="data_sync"
            type="submit">
            {{ $t('inputs.save') }}
          </default-button>
        </div>
      </badge-block>
    </form>
    </ValidationObserver>
  </div>
</template>

<script>

import {axiosCommittees, axiosUsers} from "../../../helpers/axios_requests";
import routes_mixin from "../../../mixins/routes_mixin";
import RouterLinkTo from "../../small_components/RouterLinkTo.vue";
import Switcher from "../../small_components/Switcher.vue";
import {mapActions, mapGetters} from "vuex";
import {NEW_USER} from "../../../helpers/new_objects";
import {STATUS_ERROR, STATUS_SUCCESS} from "../../../store/modules/notifiocation";
import DefaultButton from "../../small_components/DefaultButton.vue";
import BadgeBlock from "../../slots/BadgeBlock.vue";
import DefaultTitle from "../../small_components/DefaultTitle.vue";
import DefaultInput from "../../small_components/DefaultInput.vue";
import DefaultSelect from "../../small_components/DefaultSelect.vue";
import {unlinkVueVariable} from "../../../helpers/global_vars";
import DefaultBox from "../../slots/DefaultBox.vue";
import {BOOL_DATA} from "../../../helpers/constants";
import {ValidationProvider, extend, ValidationObserver} from "vee-validate";
import { regex, required, required_if } from 'vee-validate/dist/rules';
import { FORMAT_ERROR_MESSAGE } from '../../../helpers/validation_error_message';
import {ROUTES_NAMES} from "../../../router";

extend('regex', {
  ...regex,
  message: 'This field must be in format: +38012345678'
});
extend('required', {
  ...required,
  message: 'This field is required'
});
extend('required_if', {
  ...required_if,
  message: 'This field is required'
});
export default {
  name: 'UsersLayout',
  mixins: [routes_mixin],
  components: {
    DefaultBox,
    DefaultSelect,
    DefaultInput,
    DefaultTitle,
    BadgeBlock,
    DefaultButton,
    RouterLinkTo,
    Switcher,
    ValidationProvider,
    ValidationObserver,
  },
  computed: {
    ...mapGetters([
      'getUser',
      'getUserRoleIsAdmin',
    ]),
    yes_no_statuses() {
      return BOOL_DATA({yes_text: 'Yes', no_text: 'No'})
    },
    get_weekdays_selectable() {
      return this.yes_no_statuses.find(el => el.id === this.user.allow_weekend_vacation)
    },
    get_id() {
      return Number(this.$route.params.id)
    },
    route_is_profile() {
      return this.$route.name === this.route_names.profile
    },
    active() {
      return [
        {id: 0, title: 'Banned'},
        {id: 1, title: 'Active'},
      ]
    },
    genders() {
      return [
        'M',
        'F',
      ]
    },
    contract_types() {
      return [
        'Employment',
        'PE',
      ]
    },
    user_active() {
      return this.active.find(el => this.user.active === el.id)
    }
  },
  data() {
    return {
      user: null,
      new_user: false,
      roles: [],
      beltPackages: [],
      selectedBeltPackage: {},
      selectedBeltBenefit: {},
      data_sync: false,
      committees: [],
      committeeManager: [],
      committeeEmployee: null,
      gender: null,
      contract_type: null,
      disabledBeltSelect: false
    }
  },
  beforeMount() {
    this.getRoles()
    this.getBeltBenefits()
    this.fetchUser(this.get_id)
    this.fetchCommittees()
  },
  methods: {
    ...mapActions([
      'actionPushNotification'
    ]),
    getRoles() {
      axiosUsers.roles()
        .then(r => {
          this.roles = r.data
        })
        .catch(e => {
          console.log(e.response.status);
        })
    },
    getBeltBenefits() {
      axiosUsers.benefits()
        .then(r => {
          this.beltPackages = r.data
        })
        .catch(e => {
          console.log(e.response.status);
        })
    },
    fillCommittees(collection) {
      this.committees = collection || []
    },
    fetchCommittees() {
      this.loading = true
      axiosCommittees.pureList({size: 100})
        .then(r => {
          this.fillCommittees(r.data.committees)
        })
        .finally(() => {
          this.loading = false
        })
    },
    fetchUser(id) {
      if (this.route_is_profile) {
        this.$set(this, 'user', unlinkVueVariable(this.getUser))
        return
      }
      if (!id) {
        this.$set(this, 'user', NEW_USER())
        this.$set(this, 'new_user', true)
        return
      }
      axiosUsers.get(id)
        .then(r => {
          if (r.data.profile === null) {
            r.data.profile = {};
          }
          this.$set(this, 'user', r.data)
          if (r.data.committee_employee) {
            this.$set(this, 'committeeEmployee', r.data.committee_employee)
          }
          if (r.data.committee_manager) {
            r.data.committee_manager.forEach(element => {
              this.committeeManager.push(element.committees);
            });
          }
          if (null != this.user.profile.contract_type) {
            this.contract_type = this.user.profile.contract_type;
          }
          if (null != this.user.profile.gender) {
            this.gender = this.user.profile.gender;
          }
          if (null != this.user.beltBenefit) {
            this.selectedBeltPackage = this.user.beltBenefit.package;
            this.selectedBeltBenefit = this.user.beltBenefit;
            this.disabledBeltSelect = true;
          }
        })
        .catch(e => {
        })
    },
    changeBeltPackage(val) {
      this.selectedBeltPackage = val;
      this.selectedBeltBenefit = {};
      this.disabledBeltSelect = false;
    },
    changeBeltBenefit(val) {
      this.selectedBeltBenefit = val;
    },
    changeRole(val) {
      this.user.role = val
    },
    changeActive({id}) {
      this.user.active = id
    },
    changeGender(val) {
      this.gender = val;
    },
    changeContractType(val) {
      this.contract_type = val;
    },
    changeCommitteeManager(val) {
      this.committeeManager = val;
    },
    changeCommitteeEmployee(val) {
      this.committeeEmployee = val
    },
    createUpdate() {
      this.loading = true
      this.data_sync = true
      this.prepareUserRequest();
      axiosUsers.createUpdate(this.user)
        .then(({data}) => {
          if (data.errors) {
            this.$refs.form.setErrors(data.errors);
            let message = FORMAT_ERROR_MESSAGE(data.errors)
            this.actionPushNotification(
                {message: message, status: STATUS_ERROR}
            )
            return;
          }
          this.actionPushNotification({message: 'Saved', status: STATUS_SUCCESS})
          if (this.get_id === Number(data.id)) return
          this.fetchUser(this.get_id)
        })
        .catch(e => {
          let message = FORMAT_ERROR_MESSAGE(
              e.data.errors
          )
          this.actionPushNotification({message: 'Error', status: STATUS_ERROR})
          if (e.response.data.errors) {
            this.$refs.form.setErrors(e.response.data.errors);
          }
        })
        .finally(() => {
          this.data_sync = false
          if (this.new_user) {
            this.$router.push({name: ROUTES_NAMES.users})
          }
          this.loading = false
        })
    },
    formatErrorNotificationFromServer(validationErrors) {
      let errors = validationErrors.errors
      let message = 'Validation Errors! \n';
      for (const key in errors) {
        message += '<b>' + key + '</b>' + ': ' + errors[key]  + "\r\n"
      }

      return message;
    },
    prepareUserRequest() {
      this.user.committee = {};
      this.user.committee.employee_of = null;
      this.user.committee.manager_of = null;
      if (
        typeof this.committeeEmployee !== 'undefined' &&
        null !== this.committeeEmployee
      ) {
        this.user.committee.employee_of = this.committeeEmployee.id
      }
      if (
        typeof this.committeeManager !== 'undefined' &&
        null !== this.committeeManager
      ) {
        let managerArray = [];
        (this.committeeManager || []).forEach(element => {
          managerArray.push(element.id);
        });
        this.user.committee.manager_of = managerArray;
      }
      this.user.profile.contract_type = this.contract_type;
      this.user.profile.gender = this.gender;

      if (null !== this.user.role &&
        typeof this.user.role === 'object'
      ) {
        this.user.role = this.user.role.name;
      }
      if (
        null !== this.selectedBeltBenefit &&
        typeof this.selectedBeltBenefit === 'object'
      ) {
        this.user.selected_benefit = this.selectedBeltBenefit.id;
      }
      console.log(this.user)
    }
  },
}
</script>
